import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { toErrorMessage } from '../../errors/api-error';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { countriesService } from '../../services/catalog.service';
import { Flag } from './Flag';

type Props = {
  open: boolean;
  /** Current selection (alpha-2).  Pre-checks that row when the dialog opens. */
  currentCountry?: string | null;
  onClose: () => void;
  /** Called with the chosen alpha-2 code when the user confirms. */
  onConfirm: (alpha2: string) => void;
};

/**
 * Country-selection modal styled after Planity / Booking.com onboarding
 * dialogs.  Lists every active country in the platform with its localised
 * name + flag emoji + radio.  The confirm button is disabled until a row
 * is selected, so the user can never confirm "nothing".
 */
export function CountrySelectionDialog({
  open,
  currentCountry,
  onClose,
  onConfirm,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale =
    (i18n.resolvedLanguage ?? i18n.language ?? 'fr').slice(0, 2).toLowerCase();

  const countries = useQuery({
    queryKey: ['countries', locale],
    queryFn: () => countriesService.list(locale),
    staleTime: 60 * 60 * 1000,
    enabled: open, // only fetch when the dialog is actually shown
    // Fail fast in the dialog — one retry is enough; longer is just the
    // user staring at skeletons wondering what's happening.
    retry: 1,
  });

  const [selected, setSelected] = useState<string>(currentCountry ?? '');

  // Re-sync the radio when the dialog is (re-)opened so it always reflects
  // the persisted choice rather than stale local state.
  useEffect(() => {
    if (open) setSelected(currentCountry ?? '');
  }, [open, currentCountry]);

  const confirmDisabled = !selected;

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // MUI v5 Dialog: `slotProps.paper` isn't typed yet, so we use
      // PaperProps for the inner Paper and slotProps for the backdrop.
      PaperProps={{ sx: { borderRadius: 3, p: { xs: 0.5, sm: 1 } } }}
      slotProps={{
        // Soft blur over whatever's behind so the dialog feels like an
        // onboarding step rather than a generic modal.
        backdrop: { sx: { backdropFilter: 'blur(4px)' } },
      }}
    >
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
          {t('country.dialogTitle')}
        </Typography>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography color="text.secondary" sx={{ mb: 2.5, fontSize: '0.9375rem' }}>
          {t('country.dialogSubtitle')}
        </Typography>

        {countries.isError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => countries.refetch()}>
                {t('common.tryAgain', { defaultValue: 'Try again' })}
              </Button>
            }
          >
            {toErrorMessage(countries.error, t)}
          </Alert>
        )}

        <Stack spacing={0.5} role="radiogroup" aria-label={t('country.dialogTitle')}>
          {countries.isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} />
              ))
            : (countries.data ?? []).map((c) => {
                const checked = selected === c.alpha2;
                return (
                  <Box
                    key={c.alpha2}
                    role="radio"
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => setSelected(c.alpha2)}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        setSelected(c.alpha2);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'background-color 150ms ease, border-color 150ms ease',
                      border: '1px solid transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      ...(checked && {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? `${theme.palette.primary.main}1F`
                            : `${theme.palette.primary.main}0F`,
                        borderColor: 'primary.main',
                      }),
                    }}
                  >
                    <Radio
                      checked={checked}
                      tabIndex={-1}
                      sx={{ p: 0.5 }}
                      inputProps={{ 'aria-label': c.name }}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        fontWeight: checked ? 600 : 500,
                        fontSize: '1rem',
                      }}
                    >
                      {c.name}
                    </Typography>
                    <Flag alpha2={c.alpha2} size="1.75rem" />
                  </Box>
                );
              })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={confirmDisabled}
          onClick={handleConfirm}
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1A1816',
            color: 'common.white',
            fontWeight: 700,
            py: 1.5,
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.dark
                  : '#000000',
            },
            '&.Mui-disabled': { opacity: 0.4 },
          }}
        >
          {t('country.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
